import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useIsDarkMode } from "../../theme/ThemeContext";
import {
  ConnectionMap,
  LATENCY_TONES,
  PingHistory,
  TweenNumber,
  toneFor,
} from "./StatusPulse";

// Public status page (/status, linked from the footer). Intentionally not
// behind ADFS auth so it stays reachable when sign-in itself is broken.
// Latency is sampled from this browser against /api/status/ping; uptime %s
// come from server heartbeats via /api/status.

const PING_INTERVAL_MS = 3000;
const INFO_INTERVAL_MS = 30000;
const MAX_POINTS = 100; // ~5 minutes of samples at 3s

const STATUS_STYLES = {
  operational: { label: "All Systems Operational", className: "bg-emerald-600" },
  degraded: { label: "Degraded Performance", className: "bg-amber-600" },
  down: { label: "Server Unreachable", className: "bg-red-600" },
  checking: { label: "Checking…", className: "bg-slate-500" },
};

const timeLabel = (date) =>
  date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const formatPct = (value) =>
  value == null ? "—" : value >= 99.995 ? "100%" : `${value.toFixed(2)}%`;

const formatDuration = (totalSec) => {
  if (totalSec == null) return "—";
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const StatCard = ({ label, value, sub }) => (
  <div className="app-card rounded-2xl border p-5">
    <p className="app-eyebrow">{label}</p>
    <p className="mt-1.5 text-2xl font-bold tabular-nums tracking-tight sm:text-3xl">
      {value}
    </p>
    {sub && <p className="app-muted mt-1 text-xs leading-relaxed">{sub}</p>}
  </div>
);

const SiteStatus = () => {
  const isDark = useIsDarkMode();
  const [samples, setSamples] = useState([]);
  const [info, setInfo] = useState(null);
  const [infoFailed, setInfoFailed] = useState(false);
  // Stable per-sample ids so the animation can tell a new ping from a re-render.
  const sampleId = useRef(0);

  // Live latency sampler: round trip from this browser to the server.
  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const startedAt = performance.now();
      let ms = null;
      try {
        const res = await fetch("/api/status/ping", { cache: "no-store" });
        if (res.ok) ms = Math.round(performance.now() - startedAt);
      } catch (err) {
        // failed ping -> null sample, drawn as a red packet / bar
      }
      if (cancelled) return;
      sampleId.current += 1;
      const id = sampleId.current;
      setSamples((prev) =>
        [...prev, { id, time: timeLabel(new Date()), ms }].slice(-MAX_POINTS)
      );
    };
    ping();
    const interval = setInterval(ping, PING_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Uptime %s, process uptime, and DB health from the server.
  useEffect(() => {
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        const res = await fetch("/api/status", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setInfo(json);
        setInfoFailed(false);
      } catch (err) {
        if (!cancelled) setInfoFailed(true);
      }
    };
    fetchInfo();
    const interval = setInterval(fetchInfo, INFO_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const stats = useMemo(() => {
    const good = samples.filter((s) => s.ms != null).map((s) => s.ms);
    const recent = samples
      .slice(-10)
      .filter((s) => s.ms != null)
      .map((s) => s.ms);
    return {
      last: samples.length ? samples[samples.length - 1].ms : null,
      recentAvg: recent.length
        ? Math.round(recent.reduce((a, b) => a + b, 0) / recent.length)
        : null,
      max: good.length ? Math.max(...good) : null,
      failed: samples.length - good.length,
    };
  }, [samples]);

  const status = useMemo(() => {
    if (samples.length === 0) return "checking";
    const lastTwo = samples.slice(-2);
    if (lastTwo.length === 2 && lastTwo.every((s) => s.ms == null)) {
      return "down";
    }
    if (samples[samples.length - 1].ms == null) return "degraded";
    if (info && info.db && !info.db.ok) return "degraded";
    if (stats.recentAvg != null && stats.recentAvg > 750) return "degraded";
    return "operational";
  }, [samples, info, stats]);

  const pill = STATUS_STYLES[status];
  const failedWindowMin = Math.max(
    1,
    Math.round((samples.length * PING_INTERVAL_MS) / 60000)
  );
  const nowTone = samples.length
    ? toneFor(samples[samples.length - 1].ms)
    : null;
  const serverUp = status !== "down";

  return (
    <div className="app-page min-h-screen">
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:py-10">
        <div className="flex items-center justify-between gap-3">
          <img
            src={
              isDark
                ? require("../../assets/NodeLogo2.png")
                : require("../../assets/NodeLogoDark.png")
            }
            alt="Big C Node"
            className="h-7 w-auto"
          />
          <Link to="/" className="app-link text-sm">
            ← Back to Big C Node
          </Link>
        </div>

        <div className="mb-6 mt-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="app-eyebrow">System status</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
              Big C Node Status
            </h1>
            <p className="app-muted mt-1">
              Live health of the site, server, and database.
            </p>
          </div>
          <span
            role="status"
            className={`inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm sm:self-auto ${pill.className}`}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
            </span>
            {pill.label}
          </span>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Uptime — Last 24 Hours"
            value={formatPct(info?.uptime?.day)}
          />
          <StatCard
            label="Uptime — Last 7 Days"
            value={formatPct(info?.uptime?.week)}
          />
          <StatCard
            label="Uptime — Last 30 Days"
            value={formatPct(info?.uptime?.month)}
          />
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard
            label="Server"
            value={serverUp ? "Online" : "Unreachable"}
            sub={
              info
                ? `Running for ${formatDuration(info.processUptimeSec)} (since ${new Date(info.startedAt).toLocaleString()})`
                : infoFailed
                  ? "Status details unavailable"
                  : "Loading…"
            }
          />
          <StatCard
            label="Database"
            value={info ? (info.db?.ok ? "Connected" : "Not Connected") : "—"}
            sub={
              info?.db?.ok
                ? `Query round trip: ${info.db.latencyMs} ms`
                : "No response from the database"
            }
          />
        </div>

        <div className="app-card overflow-hidden rounded-2xl border p-5 sm:p-6">
          <div className="mb-2 flex flex-col justify-between gap-1 sm:flex-row sm:items-end">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Live Connection
              </h2>
              <p className="app-muted text-xs">
                Every {PING_INTERVAL_MS / 1000} seconds your browser pings the
                server — watch each round trip travel there and back.
              </p>
            </div>
            {nowTone && (
              <span
                className="inline-flex items-center gap-1.5 self-start text-xs font-semibold sm:self-auto"
                style={{ color: LATENCY_TONES[nowTone].color }}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: LATENCY_TONES[nowTone].color }}
                />
                {LATENCY_TONES[nowTone].label}
              </span>
            )}
          </div>

          <ConnectionMap
            samples={samples}
            status={status}
            dbOk={info ? Boolean(info.db?.ok) : undefined}
            labels={{
              you: {
                title: "Your browser",
                sub:
                  stats.last != null
                    ? `${stats.last} ms round trip`
                    : samples.length
                      ? "No response"
                      : "Pinging…",
              },
              server: {
                title: "Node server",
                sub: status === "checking" ? "Checking…" : serverUp ? "Online" : "Unreachable",
              },
              db: {
                title: "Database",
                sub: info
                  ? info.db?.ok
                    ? `Connected · ${info.db.latencyMs} ms`
                    : "Not connected"
                  : infoFailed
                    ? "Unknown"
                    : "Checking…",
              },
            }}
          />

          <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: "Now", value: stats.last, tone: nowTone },
              { label: "Average", value: stats.recentAvg, hint: "last 10 pings" },
              { label: "Peak", value: stats.max, hint: "this visit" },
            ].map((tile) => (
              <div key={tile.label} className="app-surface rounded-xl border px-3 py-2.5 text-center">
                <p className="app-eyebrow">{tile.label}</p>
                <p
                  className="mt-0.5 text-xl font-bold tabular-nums tracking-tight sm:text-2xl"
                  style={
                    tile.tone && tile.value != null
                      ? { color: LATENCY_TONES[tile.tone].color }
                      : undefined
                  }
                >
                  <TweenNumber value={tile.value} suffix=" ms" />
                </p>
                {tile.hint && (
                  <p className="app-muted hidden text-[11px] sm:block">{tile.hint}</p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-5">
            <p className="app-eyebrow mb-2">Recent pings</p>
            <PingHistory
              samples={samples}
              slots={MAX_POINTS}
              intervalMs={PING_INTERVAL_MS}
            />
          </div>

          {stats.failed > 0 && (
            <p className="mt-4 rounded-xl bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-700 dark:text-rose-300">
              {stats.failed} failed ping{stats.failed === 1 ? "" : "s"} in the
              last {failedWindowMin} minute{failedWindowMin === 1 ? "" : "s"}{" "}
              (the red bars above).
            </p>
          )}
        </div>

        <p className="app-muted mt-4 text-xs">
          Uptime is measured from server heartbeats recorded once per minute
          {info?.measuringSince &&
            `, tracked since ${new Date(info.measuringSince).toLocaleDateString()}`}
          . This page refreshes automatically.
        </p>
      </div>
    </div>
  );
};

export default SiteStatus;
